import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Center,
  Container,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import {
  IconBrandGooglePlay,
  IconCircleCheck,
  IconDeviceMobile,
  IconInfoCircle,
  IconLock,
  IconMail,
  IconMapPin,
  IconPhone,
  IconUser,
} from "@tabler/icons-react";
import { DRIVER_SELF_REGISTRATION } from "apollo/mutuations";
import { GET_REGIONS } from "apollo/queries";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Form, 2: Success Message
  const [regionsDropDownData, setRegionsDropDownData] = useState([]);

  // --- Form Hook (Consistent with Add Driver Modal) ---
  const form = useForm({
    initialValues: {
      name: "",
      phone: "",
      email: "",
      city: "",
      region: "",
      password: "",
      password_confirmation: "",
    },
    validate: {
      phone: (value) =>
        value.length > 13 || value.length < 10
          ? "Phone number must be between 10-13 characters"
          : null,
      password: (value) => {
        if (!value) return "Password is required";
        if (value.length < 8) return "Password must be at least 8 characters";
        if (!/[A-Z]/.test(value))
          return "Password must contain at least one uppercase letter";
        if (!/[0-9]/.test(value))
          return "Password must contain at least one number";
        if (!/[^A-Za-z0-9]/.test(value))
          return "Password must contain at least one special character";
        return null;
      },
      password_confirmation: (value, values) =>
        value !== values.password ? "Passwords do not match" : null,
      region: (value) => (!value ? "Please select a region" : null),
    },
  });

  // --- Queries ---
  const { loading: regionsLoading } = useQuery(GET_REGIONS, {
    variables: { first: 100, page: 1 },
    onCompleted: (data) => {
      const formatted = data.regions.data.map((r) => ({
        label: r.name,
        value: r.id.toString(),
      }));
      setRegionsDropDownData(formatted);
    },
  });

  // --- Mutations ---
  const [createAccount, { loading: creating }] = useMutation(
    DRIVER_SELF_REGISTRATION,
    {
      onCompleted: () => {
        showNotification({
          title: "Registration Successful",
          message: "Your application has been submitted.",
          color: "green",
          icon: <IconCircleCheck />,
        });
        setStep(2); // Move to the success information screen
      },
      onError: (error) => {
        showNotification({
          title: "Registration Failed",
          message: error.message,
          color: "red",
        });
      },
    },
  );

  // --- Handlers ---
  const handleRegister = (values) => {
    createAccount({
      variables: {
        region_id: values.region,
        name: values.name,
        city: values.city,
        phone: values.phone,
        email: values.email,
        password: values.password,
        password_confirmation: values.password_confirmation,
      },
    });
  };

  return (
    <Container size={460} my={40}>
      <Paper
        withBorder
        shadow="md"
        p={30}
        radius="md"
        sx={{ position: "relative", minHeight: step === 2 ? "300px" : "auto" }}
      >
        <LoadingOverlay visible={creating || regionsLoading} overlayBlur={2} />

        {step === 1 ? (
          <>
            <Title
              align="center"
              sx={(theme) => ({
                fontWeight: 900,
                fontFamily: `Greycliff CF, ${theme.fontFamily}`,
              })}
            >
              Create Account
            </Title>
            <Text color="dimmed" size="sm" align="center" mt={5} mb="xl">
              Join our team by filling out the details below
            </Text>

            <form onSubmit={form.onSubmit(handleRegister)}>
              <Stack>
                <TextInput
                  label="Full Name"
                  required
                  placeholder=""
                  icon={<IconUser size={16} />}
                  {...form.getInputProps("name")}
                />

                <TextInput
                  label="Phone Number"
                  required
                  placeholder="09..."
                  icon={<IconPhone size={16} />}
                  {...form.getInputProps("phone")}
                />

                <TextInput
                  label="Email"
                  placeholder="email@example.com"
                  icon={<IconMail size={16} />}
                  {...form.getInputProps("email")}
                />

                <Select
                  label="Region"
                  placeholder="Select your region"
                  required
                  icon={<IconMapPin size={16} />}
                  data={regionsDropDownData}
                  {...form.getInputProps("region")}
                />

                <TextInput
                  label="City"
                  required
                  placeholder="Your City"
                  {...form.getInputProps("city")}
                />

                <PasswordInput
                  label="Password"
                  required
                  icon={<IconLock size={16} />}
                  {...form.getInputProps("password")}
                />

                <PasswordInput
                  label="Confirm Password"
                  required
                  icon={<IconLock size={16} />}
                  {...form.getInputProps("password_confirmation")}
                />

                <Button
                  type="submit"
                  fullWidth
                  mt="md"
                  size="md"
                  sx={{
                    backgroundColor: "#FF6A00",
                    "&:hover": { backgroundColor: "#e55f00" },
                  }}
                >
                  Register
                </Button>

                <Button
                  variant="subtle"
                  fullWidth
                  size="xs"
                  color="gray"
                  onClick={() => navigate("/login")}
                >
                  Already have an account? Login
                </Button>
              </Stack>
            </form>
          </>
        ) : (
          /* SUCCESS STATE: Information for Admin Approval */
          <Stack align="center" spacing="lg" py="xl">
            <ThemeIcon color="green" size={60} radius="xl">
              <IconCircleCheck size={40} />
            </ThemeIcon>

            <div style={{ textAlign: "center" }}>
              <Title order={2}>Registration Received!</Title>
              <Text color="dimmed" mt="sm">
                Your account has been created successfully.
              </Text>
            </div>

            <Paper
              withBorder
              p="md"
              radius="md"
              sx={(theme) => ({ backgroundColor: theme.colors.blue[0] })}
            >
              <Group noWrap align="flex-start">
                <IconInfoCircle
                  size={20}
                  color="#228be6"
                  style={{ marginTop: 3 }}
                />
                <Text size="sm" color="blue">
                  To protect our platform, all new accounts must be verified.
                  <b> Please contact the system administrator</b> to provide
                  your documents and activate your account.
                </Text>
              </Group>
            </Paper>
          </Stack>
        )}

        <Divider label="OR" labelPosition="center" my="lg" />

        <Button
          component="a"
          href="https://play.google.com/store/apps/details?id=your.package.name"
          target="_blank"
          variant="outline"
          color="dark"
          size="md"
          fullWidth
          leftIcon={<IconBrandGooglePlay size={20} />}
          styles={(theme) => ({
            root: {
              backgroundColor: theme.colors.dark[6],
              color: theme.white,
              "&:hover": { backgroundColor: theme.colors.dark[7] },
            },
          })}
        >
          Get it on Google Play
        </Button>
      </Paper>

      <Center mt="xl">
        <Group spacing={5}>
          <IconDeviceMobile size={16} color="gray" />
          <Text size="xs" color="dimmed">
            Available for Android 14.0+
          </Text>
        </Group>
      </Center>
    </Container>
  );
};

export default RegisterPage;
